/**
 * StatusChip — small pill that surfaces the user's mastery status
 * for a vocab / expression catalog item.
 *
 * Used by RolePlayTemplatePreviewScreen next to each catalog item.
 * Sized to sit inline with the term; flexShrink on the term text
 * lets long lemmas truncate before the chip wraps.
 *
 * Theming: all colors come from `useDisplayTheme` (no Gluestack
 * `$token` props). Honors fontScale and the user's font-family
 * preference (`ffs`).
 */

import React from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { useDisplayTheme } from '../../hooks/useDisplayTheme';

export type StatusChipStatus = 'new' | 'learning' | 'mastered';

interface StatusChipProps {
  status: StatusChipStatus;
}

export const StatusChip = React.memo(function StatusChip({ status }: StatusChipProps) {
  const { theme, fontScale, ffs } = useDisplayTheme();
  const { t } = useTranslation();
  const tone =
    status === 'mastered'
      ? { bg: theme.success ?? theme.primary, fg: '#fff' }
      : status === 'learning'
        ? { bg: theme.primary, fg: '#fff' }
        : { bg: theme.background, fg: theme.textSecondary };
  const label =
    status === 'mastered'
      ? t('roleplay.templates.preview.statusMastered')
      : status === 'learning'
        ? t('roleplay.templates.preview.statusLearning')
        : t('roleplay.templates.preview.statusNew');
  return (
    <View
      style={[
        styles.chip,
        { backgroundColor: tone.bg, borderColor: status === 'new' ? theme.border : tone.bg },
      ]}
    >
      <Text
        allowFontScaling={false}
        style={{ fontSize: 11 * fontScale, color: tone.fg, fontWeight: '600', ...ffs }}
      >
        {label}
      </Text>
    </View>
  );
});

const styles = StyleSheet.create({
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 10,
    borderWidth: 1,
    alignSelf: 'flex-start',
  },
});
